import Service, { inject as service } from '@ember/service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

/**
 * Results Calculator Service
 *
 * Calculates race results from timing data and reference data.
 * Mirrors the Entry.getResultRecord() logic from the backend exactly.
 *
 * All lookups use UUID-based keys:
 * - Entries looked up by entry.uuid
 * - Timing records looked up by entry.uuid (entryUuid field)
 * - Classes looked up via entry.classUuid
 * - ClassGroups looked up via class.classGroupUuid
 */
export default class ResultsCalculatorService extends Service {
  @service eventData;

  // Constant for blending own pace with peer pace (0 = own pace only, 1 = peer pace only)
  PEER_WEIGHT = 0.5;
  // Window for finding peer racers (±30 seconds at same timing point)
  PEER_WINDOW_MS = 30000;

  /**
   * Calculate results for all entries in a classgroup
   * Returns array of result objects matching Entry.getResultRecord() structure
   * @param {string} eventId - Event UUID
   * @param {string} classGroupId - ClassGroup UUID
   * @param {Object} options - Optional configuration
   * @param {boolean} options.showPredictions - Whether to include predictions in place calculations
   */
  calculateResultsForClassGroup(eventId, classGroupId, options = {}) {
    const { showPredictions = false } = options;
    // Access _dataVersion to establish tracking dependency for reactivity
    // eslint-disable-next-line no-unused-vars
    const version = this.eventData._dataVersion;

    const eventDataObj = this.eventData.getEventData(eventId);
    if (!eventDataObj) {
      console.warn(`[ResultsCalculator] Event ${eventId} data not found`);
      return [];
    }

    // Check if we have the minimum required data (entries and classes)
    if (eventDataObj.entries.size === 0) {
      console.warn(`[ResultsCalculator] Event ${eventId} has no entries loaded yet`);
      return [];
    }

    const entries = this.eventData.getEntriesForClassGroup(eventId, classGroupId);
    const course = this.eventData.getCourseForClassGroup(eventId, classGroupId);
    const event = eventDataObj.event;

    // Use default precision if event metadata not available
    const precision = event?.precision || 1000;

    console.log(`[ResultsCalculator] Calculating results for classgroup ${classGroupId}:`, {
      entriesCount: entries.length,
      startRecordsCount: eventDataObj.startRecords.size,
      finishRecordsCount: eventDataObj.finishRecords.size,
      hasEvent: !!event,
      precision
    });

    if (entries.length === 0) {
      console.warn(`[ResultsCalculator] No entries found for classgroup ${classGroupId}`);
      return [];
    }

    // Build expected timing points from course
    const expectedTimingPoints = this.buildExpectedTimingPoints(course);

    // Calculate result for each entry
    const results = entries.map(entry => {
      return this.calculateEntryResult(eventDataObj, entry, expectedTimingPoints, precision);
    });

    // Sort results matching BullittTime Live sorting logic:
    // 1. Results with finish time and OK status come first, sorted by time
    // 2. Results in progress (with splits but no finish) come next, sorted by furthest split
    // 3. Results with no timing data come last, sorted by bib
    results.sort((a, b) => {
      const aTime = a.totalTimeInMilliseconds;
      const bTime = b.totalTimeInMilliseconds;

      // If both have totalTimes and are OK, sort by them
      if (aTime != null && bTime != null && a.status === 'OK' && b.status === 'OK') {
        if (aTime !== bTime) {
          return aTime > bTime ? 1 : -1;
        }
        // Use tie order if present
        if (a.tieOrder !== null && b.tieOrder !== null) {
          return a.tieOrder - b.tieOrder;
        }
        return a.uuid > b.uuid ? 1 : -1;
      }

      // If a has a totalTime and is OK, it is on top
      if (aTime != null && a.status === 'OK') {
        return -1;
      }
      // If b has a totalTime and is OK, it is on top
      if (bTime != null && b.status === 'OK') {
        return 1;
      }

      // Neither has a finish time - look at splits to determine who is furthest along
      const aSplits = a.splits || [];
      const bSplits = b.splits || [];

      let aNumSplits = 0;
      let bNumSplits = 0;
      let aLastSplitIndex = -1;
      let bLastSplitIndex = -1;

      aSplits.forEach((split, index) => {
        if (split.elapsed != null) {
          aNumSplits++;
          if (index > aLastSplitIndex) {
            aLastSplitIndex = index;
          }
        }
      });

      bSplits.forEach((split, index) => {
        if (split.elapsed != null) {
          bNumSplits++;
          if (index > bLastSplitIndex) {
            bLastSplitIndex = index;
          }
        }
      });

      // If neither has splits, sort by bib
      if (aNumSplits === 0 && bNumSplits === 0) {
        if (a.bib != null && b.bib != null) {
          return a.bib > b.bib ? 1 : -1;
        }
        return a.uuid > b.uuid ? 1 : -1;
      }

      // More splits = further along = higher in results
      if (aNumSplits !== bNumSplits) {
        return aNumSplits > bNumSplits ? -1 : 1;
      }

      // Same number of splits - sort by elapsed time at last split
      const aLastElapsed = aSplits[aLastSplitIndex]?.elapsed;
      const bLastElapsed = bSplits[bLastSplitIndex]?.elapsed;

      if (aLastElapsed != null && bLastElapsed != null) {
        return aLastElapsed > bLastElapsed ? 1 : aLastElapsed < bLastElapsed ? -1 : 0;
      }

      return 0;
    });

    // Assign places - ONLY to entries with status === 'OK' AND totalTimeInMilliseconds !== null
    let currentPlace = 1;
    let lastTime = null;
    let tiePlace = null;

    for (const result of results) {
      // Initialize place to empty string for all results
      result.place = "";

      // Only assign place if status is OK and has a time
      if (result.status === 'OK' && result.totalTimeInMilliseconds !== null) {
        // Handle ties
        if (result.totalTimeInMilliseconds === lastTime) {
          // This is a tie
          if (tiePlace === null) {
            tiePlace = currentPlace - 1;
          }
          result.place = tiePlace;
        } else {
          tiePlace = null;
          result.place = currentPlace;
        }

        currentPlace++;
        lastTime = result.totalTimeInMilliseconds;
      }
    }

    // Calculate predicted times for all results
    this.calculateAllPredictedTimes(results, course, precision);

    // Calculate places at each timing point based on elapsed time
    // Only include predictions in place calculations if they will be displayed
    this.calculateSplitPlaces(results, course, showPredictions);

    // Calculate places for predicted finish times (only when showPredictions is enabled)
    if (showPredictions) {
      this.calculatePredictedFinishPlaces(results);
    }

    return results;
  }

  /**
   * Calculate places at each timing point based on elapsed (cumulative) time
   * @param {Array} results - Array of result objects
   * @param {Object} course - Course object with courseElements
   * @param {boolean} showPredictions - Whether to include predicted times in place calculations
   */
  calculateSplitPlaces(results, course, showPredictions = false) {
    if (!course) return;

    const courseElements = course?.courseElements || course?.CourseElements || [];
    const publicElements = courseElements
      .filter(ce => ce.intermediateTimingPointUuid !== null && ce.isPublic)
      .sort((a, b) => a.order - b.order);

    // For each public timing point, calculate places
    for (const element of publicElements) {
      const elementUuid = element.uuid;

      // Collect all results with times at this point
      // Only include predicted times if showPredictions is enabled
      const timesAtPoint = [];
      for (const result of results) {
        if (result.status !== 'OK') continue;

        const splitData = result.splitsMap?.[elementUuid];
        const predictedData = result.predictedSplits?.[elementUuid];

        let elapsed = null;
        let isPredicted = false;

        if (splitData?.elapsed != null) {
          // Actual time - always include
          elapsed = splitData.elapsed;
        } else if (showPredictions && predictedData?.elapsed != null) {
          // Predicted time - only include if showPredictions is enabled
          elapsed = predictedData.elapsed;
          isPredicted = true;
        }

        if (elapsed != null) {
          timesAtPoint.push({
            result,
            elapsed,
            isPredicted,
            elementUuid
          });
        }
      }

      // Sort by elapsed time
      timesAtPoint.sort((a, b) => a.elapsed - b.elapsed);

      // Assign places (handle ties)
      let currentPlace = 1;
      let lastElapsed = null;
      let tiePlace = null;

      for (const entry of timesAtPoint) {
        let place;
        if (entry.elapsed === lastElapsed) {
          // Tie - use same place as previous
          if (tiePlace === null) {
            tiePlace = currentPlace - 1;
          }
          place = tiePlace;
        } else {
          tiePlace = null;
          place = currentPlace;
        }

        // Store place in the appropriate data structure
        if (entry.isPredicted) {
          if (entry.result.predictedSplits?.[elementUuid]) {
            entry.result.predictedSplits[elementUuid].place = place;
          }
        } else {
          if (entry.result.splitsMap?.[elementUuid]) {
            entry.result.splitsMap[elementUuid].place = place;
          }
        }

        currentPlace++;
        lastElapsed = entry.elapsed;
      }
    }
  }

  /**
   * Calculate places for predicted finish times
   * Combines actual finish times with predicted finish times to determine what place
   * each predicted finish would be in
   */
  calculatePredictedFinishPlaces(results) {
    // Collect all results with either actual or predicted finish times
    const finishTimes = [];
    for (const result of results) {
      if (result.status !== 'OK') continue;

      if (result.totalTimeInMilliseconds != null) {
        // Actual finish time
        finishTimes.push({
          result,
          time: result.totalTimeInMilliseconds,
          isPredicted: false
        });
      } else if (result.predictedFinish != null) {
        // Predicted finish time
        finishTimes.push({
          result,
          time: result.predictedFinish,
          isPredicted: true
        });
      }
    }

    // Sort by time
    finishTimes.sort((a, b) => a.time - b.time);

    // Assign places (handle ties)
    let currentPlace = 1;
    let lastTime = null;
    let tiePlace = null;

    for (const entry of finishTimes) {
      let place;
      if (entry.time === lastTime) {
        // Tie - use same place as previous
        if (tiePlace === null) {
          tiePlace = currentPlace - 1;
        }
        place = tiePlace;
      } else {
        tiePlace = null;
        place = currentPlace;
      }

      // Only store place on predicted finishes (actual finishes already have places)
      if (entry.isPredicted) {
        entry.result.predictedFinishPlace = place;
      }

      currentPlace++;
      lastTime = entry.time;
    }
  }

  /**
   * Calculate predicted times for all results in a classgroup
   * Must be called after results are sorted and have splits calculated
   */
  calculateAllPredictedTimes(results, course, precision) {
    if (!course) return;

    const courseElements = course?.courseElements || course?.CourseElements || [];
    const publicElements = courseElements
      .filter(ce => ce.intermediateTimingPointUuid !== null && ce.isPublic)
      .sort((a, b) => a.order - b.order);

    const courseLength = parseInt(course.length) || 0;

    // Need course length and at least one timing point for predictions
    if (!courseLength || publicElements.length === 0) return;

    // Get finished results for peer pace calculation
    const finishedResults = results.filter(r => r.status === 'OK' && r.totalTimeInMilliseconds);

    // Calculate predictions for each result that isn't finished
    for (const result of results) {
      if (result.status === 'OK' && result.totalTimeInMilliseconds) {
        // Already finished, no predictions needed
        continue;
      }

      this.calculatePredictedTimes(result, finishedResults, publicElements, courseLength, precision);
    }
  }

  /**
   * Calculate predicted times for a single result
   */
  calculatePredictedTimes(result, finishedResults, publicElements, courseLength, precision) {
    // Find the last timing point where this racer has data
    const lastKnown = this.getLastKnownSplit(result, publicElements);
    if (!lastKnown) return; // No timing data yet

    const distanceFromStart = lastKnown.element.length;
    if (!distanceFromStart || distanceFromStart <= 0) return; // No distance data

    // Calculate own pace (ms per meter)
    const ownPace = lastKnown.elapsed / distanceFromStart;

    // Calculate peer pace from finished racers with similar elapsed at same point
    const peerPace = this.calculatePeerPace(
      lastKnown,
      finishedResults,
      courseLength
    );

    // Blend paces
    let blendedPace;
    if (peerPace !== null) {
      blendedPace = ownPace * (1 - this.PEER_WEIGHT) + peerPace * this.PEER_WEIGHT;
    } else {
      blendedPace = ownPace;
    }

    // Initialize predicted splits map
    result.predictedSplits = {};

    // Calculate predicted times for remaining timing points
    const lastKnownOrder = lastKnown.element.order;
    for (const element of publicElements) {
      if (element.order <= lastKnownOrder) continue; // Already passed this point
      if (!element.length) continue; // No distance data for this element

      const additionalDistance = element.length - distanceFromStart;
      const predictedElapsed = lastKnown.elapsed + (additionalDistance * blendedPace);

      result.predictedSplits[element.uuid] = {
        elapsed: predictedElapsed,
        elapsedFormatted: this.formatTime(predictedElapsed, precision),
        isPredicted: true
      };
    }

    // Calculate predicted finish time
    const distanceToFinish = courseLength - distanceFromStart;
    if (distanceToFinish > 0) {
      result.predictedFinish = lastKnown.elapsed + (distanceToFinish * blendedPace);
      result.predictedFinishFormatted = this.formatTime(result.predictedFinish, precision);
    }
  }

  /**
   * Get the last timing point where a racer has elapsed time data
   */
  getLastKnownSplit(result, publicElements) {
    if (!result.splits || result.splits.length === 0) return null;

    let lastKnown = null;

    for (const element of publicElements) {
      const split = result.splitsMap?.[element.uuid];
      if (split?.elapsed) {
        lastKnown = {
          element: element,
          elapsed: split.elapsed,
          courseElementUuid: element.uuid
        };
      }
    }

    return lastKnown;
  }

  /**
   * Calculate average pace of peer racers (finished racers with similar elapsed at same point)
   * Returns pace in ms per meter, or null if no peers found
   */
  calculatePeerPace(lastKnown, finishedResults, courseLength) {
    const distanceFromStart = lastKnown.element.length;
    const remainingDistance = courseLength - distanceFromStart;

    if (remainingDistance <= 0) return null;

    const peerPaces = [];

    for (const finishedResult of finishedResults) {
      // Get their elapsed time at the same timing point
      const theirSplit = finishedResult.splitsMap?.[lastKnown.courseElementUuid];
      if (!theirSplit?.elapsed) continue;

      // Check if they were within the window at this point
      const elapsedDiff = Math.abs(theirSplit.elapsed - lastKnown.elapsed);
      if (elapsedDiff > this.PEER_WINDOW_MS) continue;

      // Calculate their pace for the remaining distance
      const theirRemainingTime = finishedResult.totalTimeInMilliseconds - theirSplit.elapsed;
      const theirPace = theirRemainingTime / remainingDistance;

      peerPaces.push(theirPace);
    }

    if (peerPaces.length === 0) return null;

    // Return average peer pace
    return peerPaces.reduce((sum, p) => sum + p, 0) / peerPaces.length;
  }

  /**
   * Calculate result for a single entry
   * Matches Entry.getResultRecord() structure exactly
   * Uses UUID-based lookups for timing records and class relationships
   */
  calculateEntryResult(eventDataObj, entry, expectedTimingPoints, precision = 1000) {
    const event = eventDataObj.event;
    // Use passed precision or fallback to event precision or default
    const eventPrecision = precision || event?.precision || 1000;

    // Initialize result with all entry fields
    const result = {
      // Entry basic info (uuid-based)
      uuid: entry.uuid,
      status: entry.status,
      bib: entry.bib,
      firstName: entry.firstName,
      lastName: entry.lastName,
      name: [entry.firstName, entry.lastName].filter(Boolean).join(' '),
      id2: entry.id2 && entry.id2 !== 0 ? entry.id2 : "",
      id3: entry.id3 && entry.id3 !== 0 ? entry.id3 : "",
      id1: entry.id1,
      club: entry.club,
      town: entry.town,
      state: entry.state,
      country: entry.country,
      gender: entry.gender,
      DOB: entry.DOB,
      text1: entry.text1,
      text2: entry.text2,
      text3: entry.text3,
      pts1: entry.pts1,
      pts2: entry.pts2,
      pts3: entry.pts3,
      pts4: entry.pts4,
      pts5: entry.pts5,
      racePts1: entry.racePts1,
      racePts2: entry.racePts2,
      racePts3: entry.racePts3,
      racePts4: entry.racePts4,
      racePts5: entry.racePts5,
      eventUuid: entry.eventUuid,
      seedGroup: entry.seedGroup,

      // Timing defaults
      assignedStartTime: undefined,
      startTime: undefined,
      finishTime: undefined,
      tieOrder: null,

      // Class info (uuid-based)
      className: "",
      classUuid: null,
      classGroupName: "",
      classGroupUuid: null,
      courseUuid: null,

      // Splits and times
      splits: undefined,
      finishSplit: null,
      penaltyTimeInMilliseconds: null,
      totalTimeWithoutPenaltyInMilliseconds: null,
      penaltyTimeFormatted: "",
      totalTimeWithoutPenaltyFormatted: "",
      totalTimeInMilliseconds: null,
      totalTimeFormatted: ""
    };

    // Get timing records using entry.uuid as key
    const startRecord = eventDataObj.startRecords.get(entry.uuid);
    const finishRecord = eventDataObj.finishRecords.get(entry.uuid);
    const intermediateRecords = eventDataObj.intermediateRecords.get(entry.uuid) || [];
    const tieRecord = eventDataObj.tieTimingRecords.get(entry.uuid);
    const penaltyRecords = eventDataObj.penaltyTimingRecords.get(entry.uuid) || [];

    // Debug: log lookup for specific bib numbers
    if (entry.bib === 187 || entry.bib === '187') {
      console.log(`[ResultsCalculator] Bib 187 lookup:`, {
        entryUuid: entry.uuid,
        hasFinishRecord: !!finishRecord,
        finishRecordsKeys: Array.from(eventDataObj.finishRecords.keys()).slice(0, 10),
        finishRecord: finishRecord
      });
    }

    // Get class info using UUID-based lookups
    const entryClass = eventDataObj.classes.get(entry.classUuid);
    if (entryClass) {
      result.className = entryClass.name;
      result.classUuid = entryClass.uuid;

      const classGroup = eventDataObj.classGroups.get(entryClass.classGroupUuid);
      if (classGroup) {
        result.classGroupName = classGroup.name;
        result.classGroupUuid = classGroup.uuid;
        result.courseUuid = classGroup.courseUuid;
      }
    }

    // Start time - use flattened format (startTime field directly)
    if (startRecord) {
      result.assignedStartTime = startRecord.assignedStartTime;
      // New format uses flattened startTime directly
      result.startTime = startRecord.startTime || result.assignedStartTime;
    }

    // Finish time - use flattened format (finishTime field directly)
    if (finishRecord) {
      // New format uses flattened finishTime directly
      result.finishTime = finishRecord.finishTime;
    }

    // Tie order
    if (tieRecord) {
      result.tieOrder = tieRecord.order;
    }

    // Calculate splits
    const splits = [];
    let lastSplitTime = result.startTime;

    if (intermediateRecords.length > 0 && expectedTimingPoints && expectedTimingPoints.length > 0) {
      // Filter to only iTRs with time (flattened format uses 'time' directly)
      const iTRs = intermediateRecords.filter(iTR => iTR.time != null);

      // Sort by time
      iTRs.sort((a, b) => {
        const aTime = a.time;
        const bTime = b.time;
        return aTime > bTime ? 1 : -1;
      });

      // Match against expected timing points (using UUIDs)
      let currentIndex = 0;

      for (const eTP of expectedTimingPoints) {
        let foundTP = false;

        for (let i = currentIndex; i < iTRs.length; i++) {
          const iTR = iTRs[i];

          // Match by intermediateTimingPointUuid
          if (iTR.intermediateTimingPointUuid === eTP.intermediateTimingPointUuid) {
            let splitTime = null;

            if (eTP.isPublic) {
              splitTime = dayjs(iTR.time).diff(dayjs(lastSplitTime));
              lastSplitTime = iTR.time;
            }

            const elapsed = dayjs(iTR.time).diff(dayjs(result.startTime));
            splits.push({
              courseElementUuid: eTP.courseElementUuid,
              courseElementIndex: eTP.courseElementIndex,
              name: eTP.name,
              isPublic: eTP.isPublic,
              intermediateTimingPointUuid: eTP.intermediateTimingPointUuid,
              intermediateTimingPointName: eTP.intermediateTimingPointName,
              time: iTR.time,
              split: splitTime,
              splitFormatted: splitTime !== null ? this.formatTime(splitTime, eventPrecision) : null,
              elapsed: elapsed,
              elapsedFormatted: this.formatTime(elapsed, eventPrecision)
            });

            currentIndex = i + 1;
            foundTP = true;
            break;
          }
        }

        if (!foundTP) {
          splits.push({
            courseElementUuid: eTP.courseElementUuid,
            courseElementIndex: eTP.courseElementIndex,
            name: eTP.name,
            isPublic: eTP.isPublic,
            intermediateTimingPointUuid: eTP.intermediateTimingPointUuid,
            intermediateTimingPointName: eTP.intermediateTimingPointName,
            time: null,
            split: null,
            splitFormatted: null,
            elapsed: null,
            elapsedFormatted: null
          });
        }
      }
    }

    result.splits = splits;

    // Create splitsMap for easy lookup by course element UUID in templates
    // (keyed by courseElementUuid, not intermediateTimingPointUuid, because
    // multiple course elements can reference the same timing point in lap races)
    result.splitsMap = {};
    for (const split of splits) {
      result.splitsMap[split.courseElementUuid] = split;
    }

    // Calculate total time
    let totalTimeInMilliseconds = null;
    let penaltyInMilliseconds = 0;

    if (result.finishTime != undefined && result.startTime != undefined) {
      result.finishSplit = dayjs(result.finishTime).diff(dayjs(lastSplitTime));
      result.finishSplitFormatted = this.formatTime(result.finishSplit, eventPrecision);
      totalTimeInMilliseconds = dayjs(result.finishTime).diff(dayjs(result.startTime));

      // Floor to event precision (in 1000s of a second)
      totalTimeInMilliseconds = Math.floor(totalTimeInMilliseconds / eventPrecision) * eventPrecision;
    }

    // Calculate penalties
    if (penaltyRecords.length > 0) {
      for (const penalty of penaltyRecords) {
        // Use UTC to avoid timezone issues - penalty.time stores duration as time of day
        const pTime = dayjs.utc(penalty.time);
        const isBonus = penalty.isBonus;
        let penaltyTime = pTime.hour() * 1000 * 60 * 60 +
                          pTime.minute() * 1000 * 60 +
                          pTime.second() * 1000 +
                          pTime.millisecond();

        if (isBonus) {
          penaltyTime = -penaltyTime;
        }

        penaltyInMilliseconds += penaltyTime;
      }
    }

    result.penaltyTimeInMilliseconds = penaltyInMilliseconds;
    result.totalTimeWithoutPenaltyInMilliseconds = totalTimeInMilliseconds;

    // Format penalty time
    if (penaltyInMilliseconds !== null && penaltyInMilliseconds !== 0) {
      result.penaltyTimeFormatted = this.formatTime(penaltyInMilliseconds, eventPrecision);
    }

    // Format total time with penalty
    if (totalTimeInMilliseconds !== null) {
      totalTimeInMilliseconds += penaltyInMilliseconds;
      result.totalTimeInMilliseconds = totalTimeInMilliseconds;

      result.totalTimeFormatted = this.formatTime(totalTimeInMilliseconds, eventPrecision);
      result.totalTimeWithoutPenaltyFormatted = this.formatTime(result.totalTimeWithoutPenaltyInMilliseconds, eventPrecision);
    }

    return result;
  }

  /**
   * Build expected timing points from course
   * Uses UUID-based fields from transformed course data
   * Returns course elements with their UUIDs for unique identification
   * (multiple course elements can reference the same intermediate timing point)
   */
  buildExpectedTimingPoints(course) {
    // New format uses courseElements (camelCase), fallback to CourseElements for compatibility
    const courseElements = course?.courseElements || course?.CourseElements;
    if (!course || !courseElements) {
      return [];
    }

    return courseElements
      .filter(ce => ce.intermediateTimingPointUuid !== null)
      .sort((a, b) => a.order - b.order)
      .map((ce, index) => ({
        courseElementUuid: ce.uuid,
        courseElementOrder: ce.order,
        courseElementIndex: index,
        name: ce.name,
        isPublic: ce.isPublic,
        intermediateTimingPointUuid: ce.intermediateTimingPointUuid,
        intermediateTimingPointName: ce.intermediateTimingPointName || ce.name
      }));
  }

  /**
   * Format time based on event precision
   * Matches backend formatting exactly
   */
  formatTime(milliseconds, precision) {
    if (milliseconds === null || milliseconds === undefined) {
      return "";
    }

    // Handle negative times (penalties/bonuses)
    const isNegative = milliseconds < 0;
    const absMilliseconds = Math.abs(milliseconds);

    // Get base HH:mm:ss format
    const base = dayjs.utc(absMilliseconds).format('HH:mm:ss');
    const ms = absMilliseconds % 1000;

    let formatted;

    switch (precision) {
      case 1000: // seconds
        formatted = base;
        break;
      case 100: // tenths
        formatted = `${base}.${Math.floor(ms / 100)}`;
        break;
      case 10: // hundredths
        formatted = `${base}.${String(Math.floor(ms / 10)).padStart(2, '0')}`;
        break;
      case 1: // milliseconds
        formatted = `${base}.${String(ms).padStart(3, '0')}`;
        break;
      default:
        formatted = base;
    }

    return isNegative ? `-${formatted}` : formatted;
  }
}
